import { useTranslate } from '@va/localization';
import { InputMessage, TextInput } from '@va/ui/design-system';
import { isNil } from 'lodash';
import { forwardRef, useCallback, useImperativeHandle, useState } from 'react';
import { filterValidationKeys } from '../../constants';
import { FilterInputComponentRefType } from '../../filter-types';
import { useFiltersContext } from '../../filters-context';
import { useFilterValidation } from '../../useFilterValidation';
import { PickOperatorBtn } from '../PickOperatorBtn';
import { useOperator } from '../useOperator';
import { ComparisonFilter } from './ComparisonFilter';

export const ComparisonFilterInput = forwardRef<FilterInputComponentRefType, { filter: ComparisonFilter }>(
  ({ filter }, ref) => {
    const { operatorOptions } = filter;

    const [value, setValue] = useState<number | undefined>(filter.values[0]);
    const { operator, setOperator } = useOperator(filter.operator);

    const { applySingleNewFilter, isFilterApplied, updateAppliedFilter } = useFiltersContext();

    const translate = useTranslate();

    const onSubmit = useCallback(() => {
      if (isFilterApplied(filter.id)) {
        updateAppliedFilter(filter.id, { values: [value ?? 0], operator });
        return;
      }

      const clone = Object.assign({}, filter);
      clone.values = [value ?? 0];
      clone.operator = operator;

      applySingleNewFilter(clone);
    }, [applySingleNewFilter, filter, isFilterApplied, operator, updateAppliedFilter, value]);

    const { error, handleSubmit, validate } = useFilterValidation({
      onSubmit,
      value,
      validationFunc: (val) => {
        if (isNil(val)) {
          return filterValidationKeys.required;
        }
      },
    });

    useImperativeHandle(ref, () => ({ submit: handleSubmit }));

    const { label } = filter.inputProps ?? {};

    return (
      <>
        <div className='flex items-center w-full gap-3'>
          <PickOperatorBtn
            onOperatorChange={setOperator}
            selectedOperator={operator}
            operatorOptions={operatorOptions}
          />
          <TextInput
            wrapperClassName='flex-1 min-w-27.5'
            type='number'
            heightClass='h-12'
            clearField={() => {
              setValue(undefined);
            }}
            showIcons={false}
            value={value}
            debouncedInput={false}
            onChange={(e) => {
              const eventValue = e.target.value;

              let val;

              if (eventValue === '') {
                val = undefined;
              } else {
                val = Number(eventValue);
              }

              setValue(val);
              validate(val);
            }}
          />
          {label && <span className='text-sm text-gray-500 shrink-0'>{label}</span>}
        </div>

        {error && <InputMessage error={translate(error)} />}
      </>
    );
  },
);
